import List from '@components/v2/ui/List/List';
import ListItemText from '@components/v2/ui/List/ListItemText';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import { Stack } from '@mui/material';
import Box from '@mui/material/Box';
import { format, isValid } from 'date-fns';
import { memo, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { isDateAfter } from '../../../../utils/dates';
import { DateRangeFilterTestIds } from '../../../../utils/testIds/DateRangeFilter.testIds';
import useWidth from '../../../Hooks/useWidth';
import { FilterOptions } from '../../filter.types';
import DateFilterField from './DateFilterField';
import MinimizedDateRangeFilter from './components/MinimizedDateRangeFilter';
import {
  DateRangeFilterValue,
  DateRangeValue,
  DateShortcut,
  DateShortcutValue,
} from './dateRangeFilter.types';
import { getDateShortcuts } from './dateShortcuts';

const useStyles = makeStyles()(({ breakpoints }) => ({
  listItemText: {
    height: '20px',
    [breakpoints.down('lg')]: {
      height: '22px',
    },
  },
  boxStyle: {
    overscrollBehavior: 'none',
    maxHeight: '100%',
    overflow: 'auto',
    [breakpoints.down('xl') && breakpoints.up('sm')]: {
      maxHeight: 'min(30vh)',
    },
  },
}));

export interface DateRangeFilterV2Props extends FilterOptions<DateRangeFilterValue> {
  value: DateRangeFilterValue;
  minimized: boolean;
  filterIndex?: number;
  label?: LangEnum;
  initialDateLabel?: LangEnum;
  finalDateLabel?: LangEnum;
  onValidationError?: (hasError: boolean) => void;
  excludedShortcuts?: DateShortcut[];
  isDrawer?: boolean;
}

const PATTERN = 'yyyy-MM-dd';

const DateRangeFilterV2 = ({
  onChange,
  value,
  minimized,
  filterIndex,
  initialDateLabel = LangEnum.GLOBAL_INITIAL_DATE,
  finalDateLabel = LangEnum.GLOBAL_FINAL_DATE,
  onValidationError,
  excludedShortcuts,
  isDrawer = false,
}: DateRangeFilterV2Props) => {
  const { classes } = useStyles();
  const { mobileWidth } = useWidth();
  const { translate } = useTranslation();

  const { finalDate, initialDate } = value as DateRangeValue;
  const { shortcut: selectedShortcut } = value as DateShortcutValue;

  const initialDateInputValue =
    initialDate && isValid(initialDate) ? format(initialDate, PATTERN) : '';
  const finalDateInputValue = finalDate && isValid(finalDate) ? format(finalDate, PATTERN) : '';

  const dateShortcuts = getDateShortcuts().filter(
    (shortcut) => !excludedShortcuts?.includes(shortcut.name),
  );

  const hasError = useMemo(
    () =>
      [
        initialDate && !isValid(initialDate),
        finalDate && !isValid(finalDate),
        initialDate && finalDate && isDateAfter(initialDate, finalDate),
      ].some(Boolean),
    [initialDate, finalDate],
  );

  useEffect(() => {
    onValidationError?.(hasError);

    return () => {
      onValidationError?.(false);
    };
  }, [hasError, onValidationError]);

  if (minimized) {
    return (
      <MinimizedDateRangeFilter
        onChange={onChange}
        shortcuts={dateShortcuts}
        value={value}
        filterIndex={filterIndex}
      />
    );
  }

  return (
    <Box
      sx={{
        maxHeight: mobileWidth ? 'auto' : '425px',
        flexGrow: mobileWidth ? 1 : 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'hidden',
      }}
    >
      <Stack
        spacing={2}
        sx={{ mt: 1, pl: isDrawer ? 0.5 : 0, pr: isDrawer ? 0.5 : 1.5 }}
        direction="column"
      >
        <DateFilterField
          label={initialDateLabel}
          value={initialDateInputValue}
          error={hasError}
          onChange={(newDate: Date | null) =>
            onChange({
              ...value,
              initialDate: newDate,
              shortcut: null,
              shortcutStep: 0,
            })
          }
        />
        <DateFilterField
          label={finalDateLabel}
          value={finalDateInputValue}
          error={hasError}
          onChange={(newDate: Date | null) =>
            onChange({
              ...value,
              finalDate: newDate,
              shortcut: null,
              shortcutStep: 0,
            })
          }
        />
      </Stack>
      <Box className={classes.boxStyle} sx={{ pr: isDrawer ? 0.5 : 1.5 }}>
        <List>
          {dateShortcuts.map((shortcut) => {
            return (
              <MenuItem
                key={shortcut.title}
                selected={selectedShortcut === shortcut.name}
                testId={`${DateRangeFilterTestIds.DATE_SHORTCUT_PREFIX}${shortcut.name}`}
                onClick={() =>
                  onChange({
                    finalDate: shortcut.finalDate,
                    initialDate: shortcut.initialDate,
                    shortcut: shortcut.name,
                    shortcutStep: 0,
                  })
                }
              >
                <ListItemText
                  className={classes.listItemText}
                  primary={translate(shortcut.title)}
                />
              </MenuItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default memo(DateRangeFilterV2);
